import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from 'components/Layout';
import { Box, Button, Divider, Grid2, Fade, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Link } from '@mui/material';
import { CheckCircle, DescriptionOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { ExperienceInfoMediaService } from 'core/api/services/ExperienceInfo/media';
import { useExperiences } from 'core/contexts/experience';
import { CircularIndeterminate, UploadPDFModal, PDFViewer } from '@utriper/core-ui';
import { dateTimeFormatted } from '@utriper/core';
import { toast } from 'react-toastify';

const ExperienceTermsPage = () => {
    const { t: translate, i18n } = useTranslation();
    const { id } = useParams();
    const expIndex = parseInt(id);

    const [selectedTerms, setSelectedTerms] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [open, setOpen] = useState(false);

    const { getExperiencesTerms, experiencesTerms, activeTerms, loading, updateExperience } = useExperiences();

    const handleListItemClick = (id) => {
        const selected = experiencesTerms.find((expTerms) => expTerms.id === id);
        setSelectedTerms(selected);
        setPageNumber(1);
    };

    const handleSetActive = async (id) => {
        const params = {
            id: expIndex,
            currentTermsId: id,
        };

        await updateExperience(params);
        await getExperiencesTerms(expIndex);
    };

    const handleClickButton = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onSubmit = async (values) => {
        try {
            const result = await ExperienceInfoMediaService.savePDF(expIndex, values);

            if (result) {
                toast.success(translate('experience_terms_page.success_new_policies'), {
                    position: 'top-right',
                    theme: 'colored',
                });
            } else {
                throw new Error(translate('experience_terms_page.error_new_policies'));
            }

            setOpen(false);
            getExperiencesTerms(expIndex);
        } catch (error) {
            toast.error(error, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    useEffect(() => {
        if (activeTerms?.id) {
            setSelectedTerms({ ...activeTerms });
        } else {
            setSelectedTerms(null);
        }
    }, [experiencesTerms, activeTerms]);

    useEffect(() => {
        if (!expIndex) return;
        getExperiencesTerms(expIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expIndex]);

    return (
        <Layout>
            <Box p={2}>
                <Grid2 container justifyContent="space-between" alignItems="center" my={2}>
                    <h1 style={{ display: 'inline', color: '#363740' }}>{translate('experience_terms_page.title')}</h1>
                    {!loading && (
                        <Button variant="contained" color="secondary" sx={{ fontWeight: 'bold', color: 'white' }} onClick={handleClickButton}>
                            {translate('experience_terms_page.add_new_policies')}
                        </Button>
                    )}
                </Grid2>

                {loading ? (
                    <CircularIndeterminate centered />
                ) : (
                    <Grid2 container spacing={2}>
                        <Fade in={true}>
                            <Grid2 size={{ xs: 12, md: 4 }} minHeight={{ xs: '25dvh', md: '85dvh' }}>
                                <Box sx={{ height: '100%', border: '1px solid rgba(54, 55, 64, 0.25)', borderRadius: '8px', boxShadow: '0 0 4px 0 rgba(0, 0, 0, .25)' }}>
                                    <List component="nav" disablePadding>
                                        <ListItem sx={{ color: '#363740', fontWeight: 'bold' }}>{translate('experience_terms_page.list.title')}</ListItem>
                                        <Divider />
                                        {experiencesTerms?.length > 0 ? (
                                            experiencesTerms.map((expTerms) => (
                                                <ListItem
                                                    key={expTerms.id}
                                                    disablePadding
                                                    secondaryAction={
                                                        !expTerms.isActive ? (
                                                            <Button
                                                                size="small"
                                                                onClick={() => {
                                                                    handleSetActive(expTerms.id);
                                                                }}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                {translate('experience_terms_page.list.active_button')}
                                                            </Button>
                                                        ) : (
                                                            <Grid2 container justifyContent="flex-end" alignItems="flex-end">
                                                                <ListItemIcon sx={{ minWidth: '30px' }}>
                                                                    <CheckCircle min-width="0px" sx={{ color: '#71ba6c' }} />
                                                                </ListItemIcon>
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => {
                                                                        handleSetActive(null);
                                                                    }}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    {translate('experience_terms_page.list.remove_button')}
                                                                </Button>
                                                            </Grid2>
                                                        )
                                                    }
                                                >
                                                    <ListItemButton selected={expTerms.id === selectedTerms} onClick={() => handleListItemClick(expTerms.id)}>
                                                        <ListItemIcon sx={{ minWidth: '32px' }}>
                                                            <DescriptionOutlined sx={{ color: '#363740' }} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            sx={{ color: '#363740' }}
                                                            primary={
                                                                <Grid2
                                                                    container
                                                                    flexDirection="column"
                                                                    justifyContent="center"
                                                                    alignItems="flex-start"
                                                                    sx={{
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        display: 'block',
                                                                        maxWidth: `calc(100% - ${expTerms?.isActive ? '160px' : '120px'})`,
                                                                    }}
                                                                >
                                                                    <Typography noWrap sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                                                        {expTerms?.TermsName}
                                                                    </Typography>
                                                                    <Typography noWrap sx={{ fontSize: '.7rem', opacity: 0.75 }}>
                                                                        {dateTimeFormatted(expTerms?.createdOn, i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })}
                                                                    </Typography>
                                                                </Grid2>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))
                                        ) : (
                                            <ListItem sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#363740' }}>
                                                <Typography fontSize="1rem">{translate('experience_terms_page.list.no_data')}</Typography>
                                                <Link component="button" variant="body2" onClick={handleClickButton}>
                                                    {translate('experience_terms_page.list.create_now')}
                                                </Link>
                                            </ListItem>
                                        )}
                                    </List>
                                </Box>
                            </Grid2>
                        </Fade>

                        <Fade in={true}>
                            <Grid2 size={{ xs: 12, md: 8 }} minHeight="85dvh">
                                <Box sx={{ height: '100%', border: '1px solid rgba(54, 55, 64, 0.25)', borderRadius: '8px', boxShadow: '0 0 4px 0 rgba(0, 0, 0, .25)' }}>
                                    {selectedTerms?.id ? (
                                        <PDFViewer fileUrl={`${process.env.REACT_APP_CDN_URL}${selectedTerms?.TermsURL}`} fileName={selectedTerms?.TermsName} pageNumber={pageNumber} setPageNumber={setPageNumber} />
                                    ) : (
                                        <Grid2 container justifyContent="center" alignItems="center" sx={{ position: 'relative', backgroundColor: '#f5f5f5', borderRadius: '8px', height: '100%', width: '100%' }}>
                                            <Typography>{translate('experience_terms_page.no_pdf')}</Typography>
                                        </Grid2>
                                    )}
                                </Box>
                            </Grid2>
                        </Fade>
                    </Grid2>
                )}
            </Box>

            <UploadPDFModal open={open} onClose={onClose} onSubmit={onSubmit} />
        </Layout>
    );
};

export default ExperienceTermsPage;
